import {
  Box,
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import {
  KeyboardEvent,
  MouseEvent as ReactMouseEvent,
  TouchEvent as ReactTouchEvent,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import HTMLFlipBook from "react-pageflip";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import styles from "./BookletViewer.module.scss";

const PAGE_COUNT = 52;
const PAGE_RATIO = 595.5 / 419.25;
const MOBILE_READER_MEDIA_QUERY =
  "(max-width: 767px), (pointer: coarse) and (max-height: 600px)";
const pagePath = (page: number) =>
  `/booklets/where-stories-meet/pages/page-${String(page).padStart(2, "0")}.webp`;

const matchesMobileReader = () => {
  if (typeof window === "undefined") return false;
  if (typeof window.matchMedia !== "function") return window.innerWidth <= 767;
  return window.matchMedia(MOBILE_READER_MEDIA_QUERY).matches;
};

type FlipBookHandle = {
  pageFlip: () => {
    flipNext: (corner: "top" | "bottom") => void;
    flipPrev: (corner: "top" | "bottom") => void;
  };
};

type BookPageProps = {
  page: number;
};

const BookPage = forwardRef<HTMLDivElement, BookPageProps>(({ page }, ref) => (
  <div ref={ref} className={styles.bookPage}>
    <img
      className={styles.page}
      src={pagePath(page)}
      alt={`Where Stories Meet, page ${page}`}
      loading={page <= 2 ? "eager" : "lazy"}
      draggable={false}
    />
  </div>
));

BookPage.displayName = "BookPage";

const getPageSize = () => {
  if (typeof window === "undefined") return { width: 800, height: 563 };
  const availableWidth = Math.max(280, window.innerWidth - 32);
  const availableHeight = Math.max(198, window.innerHeight - 150);
  const width = Math.floor(Math.min(920, availableWidth, availableHeight * PAGE_RATIO));
  return { width, height: Math.floor(width / PAGE_RATIO) };
};

const BookletViewer = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const bookRef = useRef<FlipBookHandle | null>(null);
  const openingScrollPosition = useRef({ x: 0, y: 0 });
  const hideControlsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const swipeStart = useRef<{ x: number; y: number } | null>(null);
  const swipeIsSingleTouch = useRef(false);
  const suppressNextTap = useRef(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(getPageSize);
  const [isMobile, setIsMobile] = useState(matchesMobileReader);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [mobileScale, setMobileScale] = useState(1);

  useEffect(() => {
    const mobileReaderQuery =
      typeof window.matchMedia === "function"
        ? window.matchMedia(MOBILE_READER_MEDIA_QUERY)
        : null;
    const updateMobileReader = () => {
      setIsMobile(mobileReaderQuery?.matches ?? window.innerWidth <= 767);
    };
    const handleResize = () => {
      setPageSize(getPageSize());
      updateMobileReader();
    };

    window.addEventListener("resize", handleResize);
    mobileReaderQuery?.addEventListener("change", updateMobileReader);
    return () => {
      window.removeEventListener("resize", handleResize);
      mobileReaderQuery?.removeEventListener("change", updateMobileReader);
    };
  }, []);

  const showControlsTemporarily = useCallback(() => {
    setControlsVisible(true);
    if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current);
    hideControlsTimer.current = setTimeout(() => setControlsVisible(false), 3000);
  }, []);

  useEffect(() => {
    if (!isOpen || !isMobile) {
      setControlsVisible(true);
      return;
    }

    showControlsTemporarily();
    return () => {
      if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current);
      if (tapTimer.current) clearTimeout(tapTimer.current);
    };
  }, [isMobile, isOpen, showControlsTemporarily]);

  const openViewer = useCallback(() => {
    openingScrollPosition.current = { x: window.scrollX, y: window.scrollY };
    onOpen();
  }, [onOpen]);

  const closeViewer = useCallback(() => {
    onClose();
    if (isMobile) {
      const { x, y } = openingScrollPosition.current;
      window.requestAnimationFrame(() => window.scrollTo(x, y));
    }
  }, [isMobile, onClose]);

  const goNext = useCallback(() => {
    if (page === PAGE_COUNT) return;
    if (!isMobile) bookRef.current?.pageFlip().flipNext("bottom");
    if (isMobile) setMobileScale(1);
    setPage((current) => Math.min(current + 1, PAGE_COUNT));
    if (isMobile) showControlsTemporarily();
  }, [isMobile, page, showControlsTemporarily]);

  const goPrevious = useCallback(() => {
    if (page === 1) return;
    if (!isMobile) bookRef.current?.pageFlip().flipPrev("bottom");
    if (isMobile) setMobileScale(1);
    setPage((current) => Math.max(current - 1, 1));
    if (isMobile) showControlsTemporarily();
  }, [isMobile, page, showControlsTemporarily]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowRight") goNext();
    if (event.key === "ArrowLeft") goPrevious();
  };

  const handleMobileTap = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!isMobile) return;
    if (suppressNextTap.current) {
      suppressNextTap.current = false;
      return;
    }
    if (event.detail > 1) {
      if (tapTimer.current) clearTimeout(tapTimer.current);
      return;
    }

    if (tapTimer.current) clearTimeout(tapTimer.current);
    tapTimer.current = setTimeout(() => {
      if (controlsVisible) {
        if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current);
        setControlsVisible(false);
      } else {
        showControlsTemporarily();
      }
    }, 220);
  };

  const handleMobileTouchStart = (event: ReactTouchEvent<HTMLDivElement>) => {
    if (!isMobile || mobileScale > 1.01 || event.touches.length !== 1) {
      swipeStart.current = null;
      swipeIsSingleTouch.current = false;
      return;
    }

    swipeStart.current = {
      x: event.touches[0].clientX,
      y: event.touches[0].clientY,
    };
    swipeIsSingleTouch.current = true;
  };

  const handleMobileTouchMove = (event: ReactTouchEvent<HTMLDivElement>) => {
    if (event.touches.length !== 1) swipeIsSingleTouch.current = false;
  };

  const handleMobileTouchEnd = (event: ReactTouchEvent<HTMLDivElement>) => {
    const start = swipeStart.current;
    swipeStart.current = null;

    if (
      !start ||
      !swipeIsSingleTouch.current ||
      mobileScale > 1.01 ||
      event.changedTouches.length !== 1
    ) {
      swipeIsSingleTouch.current = false;
      return;
    }

    swipeIsSingleTouch.current = false;
    const deltaX = event.changedTouches[0].clientX - start.x;
    const deltaY = event.changedTouches[0].clientY - start.y;
    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY) * 1.25) return;

    suppressNextTap.current = true;
    if (deltaX < 0) goNext();
    else goPrevious();
  };

  const controlsClassName = `${styles.mobileOverlay} ${
    controlsVisible ? styles.mobileOverlayVisible : ""
  }`;
  const zoomInteractionClassName = `${styles.zoomWrapper} ${
    mobileScale <= 1.01 ? styles.nativeVerticalScroll : styles.zoomedInteraction
  }`;
  const zoomContentClassName = `${styles.zoomContent} ${
    mobileScale <= 1.01 ? styles.nativeVerticalScroll : styles.zoomedInteraction
  }`;

  return (
    <>
      <Button
        variant="link"
        color="preview.primary"
        fontSize="inherit"
        fontWeight={600}
        textDecoration="underline"
        textUnderlineOffset="3px"
        onClick={openViewer}
      >
        Where Stories Meet
      </Button>
      <Modal
        isOpen={isOpen}
        onClose={closeViewer}
        size="full"
        motionPreset="scale"
        blockScrollOnMount={!isMobile}
      >
        <ModalOverlay />
        <ModalContent
          bg="#17251f"
          color="white"
          borderRadius={0}
          className={styles.viewer}
        >
          <ModalHeader
            py={3}
            pr={14}
            fontFamily="heading"
            fontSize={{ base: "xl", md: "2xl" }}
            className={controlsClassName}
          >
            Where Stories Meet
          </ModalHeader>
          <ModalCloseButton
            aria-label="Close booklet"
            top={3}
            className={controlsClassName}
          />
          <ModalBody
            p={0}
            display="flex"
            flexDirection="column"
            minH={0}
            onKeyDown={handleKeyDown}
          >
            <Box
              className={styles.stage}
              onClick={handleMobileTap}
              onTouchStart={handleMobileTouchStart}
              onTouchMove={handleMobileTouchMove}
              onTouchEnd={handleMobileTouchEnd}
            >
              {isMobile ? (
                <TransformWrapper
                  key={page}
                  initialScale={1}
                  minScale={1}
                  maxScale={4}
                  centerOnInit
                  centerZoomedOut
                  limitToBounds
                  disablePadding
                  wheel={{ disabled: true }}
                  panning={{ disabled: mobileScale <= 1.01, velocityDisabled: true }}
                  pinch={{ disabled: false }}
                  doubleClick={{ mode: "toggle", step: 0.693, animationTime: 220 }}
                  onTransformed={(_, state) => setMobileScale(state.scale)}
                >
                  <TransformComponent
                    wrapperClass={zoomInteractionClassName}
                    contentClass={zoomContentClassName}
                  >
                    <img
                      className={styles.mobilePage}
                      src={pagePath(page)}
                      alt={`Where Stories Meet, page ${page}`}
                      draggable={false}
                    />
                  </TransformComponent>
                </TransformWrapper>
              ) : (
                <HTMLFlipBook
                key={`${pageSize.width}-${pageSize.height}`}
                ref={bookRef}
                className={styles.flipBook}
                style={{}}
                width={pageSize.width}
                height={pageSize.height}
                minWidth={pageSize.width}
                maxWidth={pageSize.width}
                minHeight={pageSize.height}
                maxHeight={pageSize.height}
                size="fixed"
                startPage={page - 1}
                drawShadow
                flippingTime={700}
                usePortrait
                startZIndex={10}
                autoSize={false}
                maxShadowOpacity={0.34}
                showCover={false}
                mobileScrollSupport={false}
                clickEventForward
                useMouseEvents
                swipeDistance={30}
                showPageCorners
                disableFlipByClick={false}
                onFlip={(event: { data: number }) => setPage(event.data + 1)}
                >
                  {Array.from({ length: PAGE_COUNT }, (_, index) => (
                    <BookPage key={index + 1} page={index + 1} />
                  ))}
                </HTMLFlipBook>
              )}
            </Box>
            <HStack
              className={controlsClassName}
              justify="center"
              spacing={{ base: 3, md: 5 }}
              py={3}
              px={4}
              bg="rgba(0, 0, 0, 0.22)"
              onClick={(event) => event.stopPropagation()}
            >
              <IconButton
                aria-label="Previous page"
                icon={<span aria-hidden="true">←</span>}
                onClick={goPrevious}
                isDisabled={page === 1}
                variant="outline"
                colorScheme="whiteAlpha"
              />
              <Text minW={{ base: "7rem", md: "9rem" }} textAlign="center" aria-live="polite">
                Page {page} of {PAGE_COUNT}
              </Text>
              <IconButton
                aria-label="Next page"
                icon={<span aria-hidden="true">→</span>}
                onClick={goNext}
                isDisabled={page === PAGE_COUNT}
                variant="outline"
                colorScheme="whiteAlpha"
              />
            </HStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default BookletViewer;
